import store from 'redux/store';
import { deleteRole } from 'redux/slices/rolesSlice';
import { addToTransportExecutor } from 'actions/factories/addToTransportExecutor';
import { deleteExecutor } from 'actions/factories/deleteExecutor';
import type { ActionDefinitions, ActionResult } from 'actions/Action';
import type { ActionEntity } from 'actions/ActionEntity';
import { showEditRole } from './edit/showEditRole';

type RoleEntity = Extract<ActionEntity, { type: 'role' }>;

const isAllRole = (entities: readonly ActionEntity[]): entities is readonly RoleEntity[] => entities.every((entity) => entity.type === 'role');

/**
 * Action definitions for roles. `createRole` / `addToTransportRole` are scoped;
 * `edit` (single-only) and `delete` are contextual. `deleteRole` is keyed by
 * string id; delete now confirms via the shared executor (was an immediate,
 * unconfirmed delete).
 */
export const roleActionDefinitions: ActionDefinitions = [
  {
    id: 'createRole',
    scope: 'type',
    label: 'New Role',
    resolve: () => () => showEditRole({ mode: 'create' }),
  },
  {
    id: 'edit',
    label: 'Edit',
    resolve: (ctx): ActionResult => {
      const { entities } = ctx;
      if (!isAllRole(entities)) return 'hidden';
      if (entities.length !== 1) return 'disabled';
      const [only] = entities;
      return () => showEditRole({ mode: 'edit', id: only.id });
    },
  },
  {
    id: 'delete',
    label: 'Delete',
    resolve: (ctx): ActionResult => {
      const { entities, refresh } = ctx;
      if (!isAllRole(entities)) return 'hidden';
      if (entities.length === 0) return 'disabled';
      return deleteExecutor(entities, (entity) => store.dispatch(deleteRole(String(entity.id))), {
        refresh,
        confirmBody: 'Are you sure you want to delete this role?',
      });
    },
  },
  {
    id: 'addToTransportRole',
    label: 'Add to Transport',
    resolve: (ctx): ActionResult => {
      const { entities } = ctx;
      if (entities.length === 0) return 'disabled';
      if (!isAllRole(entities)) return 'hidden';
      const single = entities.length === 1 ? entities[0] : null;
      return addToTransportExecutor(
        'role',
        entities.map((entity) => entity.id),
        { objectLabel: single ? single.name : 'Selected roles' }
      );
    },
  },
];
